import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CrmFieldType, TaskPriority } from '@callhub/shared';
import { Button, Input } from '@/components/ui-next';
import { api } from '@/lib/api';

const CRM_FIELD_TYPES: CrmFieldType[] = [
  'text',
  'currency',
  'number',
  'date',
  'dropdown',
  'multi_select',
  'boolean',
];

const DEFAULT_STAGE_COLOR = '#3b82f6';

export function CrmBusinessSettings() {
  const queryClient = useQueryClient();
  const crmEnabled = (import.meta.env.VITE_CRM_V2_ENABLED ?? '').toLowerCase() === 'true';

  const [fieldKey, setFieldKey] = useState('');
  const [fieldLabel, setFieldLabel] = useState('');
  const [fieldType, setFieldType] = useState<CrmFieldType>('text');

  const [pipelineName, setPipelineName] = useState('');
  const [selectedPipelineId, setSelectedPipelineId] = useState<string | null>(null);
  const [stageKey, setStageKey] = useState('');
  const [stageLabel, setStageLabel] = useState('');
  const [stageColor, setStageColor] = useState(DEFAULT_STAGE_COLOR);
  const [stageSuggestTask, setStageSuggestTask] = useState(false);
  const [stageSuggestPriority, setStageSuggestPriority] = useState<TaskPriority>('medium');

  const [statusKey, setStatusKey] = useState('');
  const [statusLabel, setStatusLabel] = useState('');

  const meQuery = useQuery({
    queryKey: ['me'],
    queryFn: async () => {
      const response = await api.getMe();
      return response.data;
    },
    enabled: crmEnabled,
    retry: false,
  });

  const fieldsQuery = useQuery({
    queryKey: ['crm-fields'],
    queryFn: async () => {
      const response = await api.getCrmFields();
      return response.data;
    },
    enabled: crmEnabled,
    retry: false,
  });

  const pipelinesQuery = useQuery({
    queryKey: ['crm-pipelines'],
    queryFn: async () => {
      const response = await api.getCrmPipelines();
      return response.data;
    },
    enabled: crmEnabled,
    retry: false,
  });

  const statusesQuery = useQuery({
    queryKey: ['crm-task-statuses'],
    queryFn: async () => {
      const response = await api.getTaskStatuses();
      return response.data;
    },
    enabled: crmEnabled,
    retry: false,
  });

  const isAdmin = meQuery.data?.role === 'admin';
  const pipelines = useMemo(() => pipelinesQuery.data ?? [], [pipelinesQuery.data]);
  const selectedPipeline = useMemo(
    () => pipelines.find((pipeline) => pipeline.id === selectedPipelineId) ?? null,
    [pipelines, selectedPipelineId],
  );

  useEffect(() => {
    if (!pipelines.length) return;
    if (selectedPipelineId && pipelines.some((pipeline) => pipeline.id === selectedPipelineId))
      return;
    const nextPipeline = pipelines.find((pipeline) => pipeline.isDefault) ?? pipelines[0];
    setSelectedPipelineId(nextPipeline?.id ?? null);
  }, [pipelines, selectedPipelineId]);

  const createFieldMutation = useMutation({
    mutationFn: async () => {
      return api.createCrmField({
        key: fieldKey,
        label: fieldLabel,
        fieldType,
      });
    },
    onSuccess: async () => {
      setFieldKey('');
      setFieldLabel('');
      setFieldType('text');
      await queryClient.invalidateQueries({ queryKey: ['crm-fields'] });
    },
  });

  const toggleFieldActiveMutation = useMutation({
    mutationFn: async (payload: { fieldId: string; isActive: boolean }) => {
      return api.updateCrmField(payload.fieldId, { isActive: payload.isActive });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['crm-fields'] });
    },
  });

  const deleteFieldMutation = useMutation({
    mutationFn: async (fieldId: string) => api.deleteCrmField(fieldId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['crm-fields'] });
    },
  });

  const createPipelineMutation = useMutation({
    mutationFn: async () => {
      return api.saveCrmPipeline({ name: pipelineName });
    },
    onSuccess: async (response) => {
      setPipelineName('');
      await queryClient.invalidateQueries({ queryKey: ['crm-pipelines'] });
      const nextPipelineId = response.data.id;
      if (nextPipelineId) setSelectedPipelineId(nextPipelineId);
    },
  });

  const createStageMutation = useMutation({
    mutationFn: async () => {
      if (!selectedPipelineId) throw new Error('Select a pipeline first');
      return api.saveCrmPipelineStage(selectedPipelineId, {
        key: stageKey,
        label: stageLabel,
        color: stageColor,
        suggestTaskOnEnter: stageSuggestTask,
        suggestTaskPriority: stageSuggestTask ? stageSuggestPriority : null,
      });
    },
    onSuccess: async () => {
      setStageKey('');
      setStageLabel('');
      setStageColor(DEFAULT_STAGE_COLOR);
      setStageSuggestTask(false);
      setStageSuggestPriority('medium');
      await queryClient.invalidateQueries({ queryKey: ['crm-pipelines'] });
    },
  });

  const deleteStageMutation = useMutation({
    mutationFn: async (payload: { pipelineId: string; stageId: string }) => {
      return api.deleteCrmPipelineStage(payload.pipelineId, payload.stageId);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['crm-pipelines'] });
    },
  });

  const createStatusMutation = useMutation({
    mutationFn: async () => {
      return api.createTaskStatus({ key: statusKey, label: statusLabel });
    },
    onSuccess: async () => {
      setStatusKey('');
      setStatusLabel('');
      await queryClient.invalidateQueries({ queryKey: ['crm-task-statuses'] });
    },
  });

  const toggleStatusTerminalMutation = useMutation({
    mutationFn: async (payload: { statusId: string; isTerminal: boolean }) => {
      return api.updateTaskStatus(payload.statusId, { isTerminal: payload.isTerminal });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['crm-task-statuses'] });
    },
  });

  const deleteStatusMutation = useMutation({
    mutationFn: async (statusId: string) => api.deleteTaskStatus(statusId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['crm-task-statuses'] });
    },
  });

  if (!crmEnabled) {
    return (
      <section className="mt-5 rounded-xl border border-border bg-background/70 p-4">
        <h4 className="text-sm font-semibold text-foreground">CRM Builders</h4>
        <p className="mt-2 text-xs text-muted-foreground">
          Frontend CRM builders are disabled. Set <code>VITE_CRM_V2_ENABLED=true</code> to enable
          Field Builder, Pipeline Builder, and Task Workflow Builder.
        </p>
      </section>
    );
  }

  return (
    <section className="mt-5 space-y-4 rounded-xl border border-border bg-background/70 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h4 className="text-sm font-semibold text-foreground">CRM Builders</h4>
        {!isAdmin ? (
          <span className="rounded-full border border-amber-500/30 bg-amber-500/10 px-2 py-0.5 text-xs text-amber-700 dark:text-amber-300">
            Read-only (admin required)
          </span>
        ) : null}
      </div>

      <div className="grid gap-4 xl:grid-cols-3">
        <article className="rounded-lg border border-border bg-card p-3">
          <h5 className="text-sm font-semibold text-foreground">Field Builder</h5>
          <div className="mt-2 space-y-2">
            <Input
              value={fieldKey}
              onChange={(event) => setFieldKey(event.target.value)}
              placeholder="Field key (e.g. company_size)"
              className="bg-background"
            />
            <Input
              value={fieldLabel}
              onChange={(event) => setFieldLabel(event.target.value)}
              placeholder="Field label"
              className="bg-background"
            />
            <select
              value={fieldType}
              onChange={(event) => setFieldType(event.target.value as CrmFieldType)}
              className="h-10 w-full rounded-xl border border-border bg-background px-3 text-sm text-foreground"
            >
              {CRM_FIELD_TYPES.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
            <Button
              variant="secondary"
              onClick={() => createFieldMutation.mutate()}
              isDisabled={
                !isAdmin || createFieldMutation.isPending || !fieldKey.trim() || !fieldLabel.trim()
              }
            >
              Add Field
            </Button>
          </div>

          <div className="mt-3 space-y-2">
            {fieldsQuery.data?.map((field) => (
              <div
                key={field.id}
                className="rounded-md border border-border bg-background px-2 py-2 text-xs"
              >
                <div className="flex items-center justify-between gap-2">
                  <p className="font-medium text-foreground">
                    {field.label} <span className="text-muted-foreground">({field.fieldType})</span>
                  </p>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      className="rounded border border-border px-2 py-0.5 text-muted-foreground"
                      disabled={!isAdmin}
                      onClick={() =>
                        toggleFieldActiveMutation.mutate({
                          fieldId: field.id,
                          isActive: !field.isActive,
                        })
                      }
                    >
                      {field.isActive ? 'Disable' : 'Enable'}
                    </button>
                    <button
                      type="button"
                      className="rounded border border-border px-2 py-0.5 text-red-500"
                      disabled={!isAdmin}
                      onClick={() => deleteFieldMutation.mutate(field.id)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
                <p className="mt-1 text-muted-foreground">key: {field.key}</p>
              </div>
            ))}
          </div>
        </article>

        <article className="rounded-lg border border-border bg-card p-3">
          <h5 className="text-sm font-semibold text-foreground">Pipeline Builder</h5>
          <div className="mt-2 space-y-2">
            <Input
              value={pipelineName}
              onChange={(event) => setPipelineName(event.target.value)}
              placeholder="Pipeline name"
              className="bg-background"
            />
            <Button
              variant="secondary"
              onClick={() => createPipelineMutation.mutate()}
              isDisabled={!isAdmin || createPipelineMutation.isPending || !pipelineName.trim()}
            >
              Add Pipeline
            </Button>
          </div>

          <div className="mt-3 space-y-2">
            <select
              value={selectedPipelineId ?? ''}
              onChange={(event) => setSelectedPipelineId(event.target.value || null)}
              className="h-10 w-full rounded-xl border border-border bg-background px-3 text-sm text-foreground"
            >
              {pipelines.map((pipeline) => (
                <option key={pipeline.id} value={pipeline.id}>
                  {pipeline.name}
                </option>
              ))}
            </select>

            <Input
              value={stageKey}
              onChange={(event) => setStageKey(event.target.value)}
              placeholder="Stage key"
              className="bg-background"
            />
            <Input
              value={stageLabel}
              onChange={(event) => setStageLabel(event.target.value)}
              placeholder="Stage label"
              className="bg-background"
            />
            <Input
              value={stageColor}
              onChange={(event) => setStageColor(event.target.value)}
              placeholder="#3b82f6"
              className="bg-background"
            />
            <label className="inline-flex items-center gap-2 text-xs text-muted-foreground">
              <input
                type="checkbox"
                className="h-4 w-4 accent-brand-500"
                checked={stageSuggestTask}
                onChange={(event) => setStageSuggestTask(event.target.checked)}
              />
              Suggest follow-up task on enter
            </label>
            {stageSuggestTask ? (
              <select
                value={stageSuggestPriority}
                onChange={(event) => setStageSuggestPriority(event.target.value as TaskPriority)}
                className="h-10 w-full rounded-xl border border-border bg-background px-3 text-sm text-foreground"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            ) : null}
            <Button
              variant="secondary"
              onClick={() => createStageMutation.mutate()}
              isDisabled={
                !isAdmin || createStageMutation.isPending || !stageKey.trim() || !stageLabel.trim()
              }
            >
              Add Stage
            </Button>
          </div>

          <div className="mt-3 space-y-2">
            {(selectedPipeline?.stages ?? [])
              .slice()
              .sort((left, right) => left.position - right.position)
              .map((stage) => (
                <div
                  key={stage.id}
                  className="flex items-center justify-between rounded-md border border-border bg-background px-2 py-1.5 text-xs"
                >
                  <span className="text-foreground">{stage.label}</span>
                  <button
                    type="button"
                    className="rounded border border-border px-2 py-0.5 text-red-500"
                    disabled={!isAdmin || !selectedPipelineId}
                    onClick={() => {
                      if (!selectedPipelineId) return;
                      deleteStageMutation.mutate({
                        pipelineId: selectedPipelineId,
                        stageId: stage.id,
                      });
                    }}
                  >
                    Delete
                  </button>
                </div>
              ))}
          </div>
        </article>

        <article className="rounded-lg border border-border bg-card p-3">
          <h5 className="text-sm font-semibold text-foreground">Task Workflow Statuses</h5>
          <div className="mt-2 space-y-2">
            <Input
              value={statusKey}
              onChange={(event) => setStatusKey(event.target.value)}
              placeholder="Status key"
              className="bg-background"
            />
            <Input
              value={statusLabel}
              onChange={(event) => setStatusLabel(event.target.value)}
              placeholder="Status label"
              className="bg-background"
            />
            <Button
              variant="secondary"
              onClick={() => createStatusMutation.mutate()}
              isDisabled={
                !isAdmin ||
                createStatusMutation.isPending ||
                !statusKey.trim() ||
                !statusLabel.trim()
              }
            >
              Add Status
            </Button>
          </div>

          <div className="mt-3 space-y-2">
            {(statusesQuery.data ?? [])
              .slice()
              .sort((left, right) => left.position - right.position)
              .map((status) => (
                <div
                  key={status.id}
                  className="rounded-md border border-border bg-background px-2 py-2 text-xs"
                >
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-medium text-foreground">{status.label}</p>
                    <button
                      type="button"
                      className="rounded border border-border px-2 py-0.5 text-red-500"
                      disabled={!isAdmin}
                      onClick={() => deleteStatusMutation.mutate(status.id)}
                    >
                      Delete
                    </button>
                  </div>
                  <label className="mt-1 inline-flex items-center gap-2 text-muted-foreground">
                    <input
                      type="checkbox"
                      className="h-4 w-4 accent-brand-500"
                      checked={status.isTerminal}
                      disabled={!isAdmin}
                      onChange={(event) =>
                        toggleStatusTerminalMutation.mutate({
                          statusId: status.id,
                          isTerminal: event.target.checked,
                        })
                      }
                    />
                    Terminal status
                  </label>
                </div>
              ))}
          </div>
        </article>
      </div>
    </section>
  );
}
